import { useQueryClient } from '@tanstack/react-query';
import { useDialogs } from '@toolpad/core/useDialogs';

import { useGetPackage } from 'api/hooks/private/packages/GetPackage';
import { useUpdatePackageTerms } from 'api/hooks/private/packages/UpdatePackageTerms';
import AgreementForm, { AgreementOutputValues } from 'components/AgreementForm';

import FeedbackDialog from 'components/Dialogs/FeedbackDialog';
import { RichTextEditorProvider } from 'components/Editor/RichTextEditor/RichTextEditorProvider';
import TermsTheme from 'components/Editor/themes/TermsTheme';
import { SerializedEditorState } from 'lexical';

import { getApiErrorMessage } from 'utils/getApiErrorMessage';

interface PackageTermsProps {
    packageId: string;
}

export function PackageTerms({ packageId }: PackageTermsProps) {
    const dialogs = useDialogs();
    const queryClient = useQueryClient();
    const updatePackageTerms = useUpdatePackageTerms();

    // fetch the package
    const pkg = useGetPackage({
        variables: {
            id: packageId,
        },
        enabled: !!packageId,
    });

    const onSubmit = async (data: AgreementOutputValues) => {
        try {
            // show confirmation dialog
            const confirmed = await dialogs.confirm(`Update Package Terms of Service?`, {
                okText: 'Update',
                cancelText: 'Cancel',
            });

            // if user cancels, return
            if (!confirmed) {
                return;
            }

            const result = await updatePackageTerms.mutateAsync({
                packageId: packageId,
                data: {
                    content: data.content as SerializedEditorState
                },
            });

            if (result) {
                // invalidate the query key
                queryClient.invalidateQueries({
                    queryKey: ['package', { id: packageId }],
                });

                // show success message
                dialogs.open(FeedbackDialog, {
                    icon: 'success',
                    title: 'Package Terms Updated',
                });
            }
        } catch (error) {
            dialogs.open(FeedbackDialog, {
                title: 'Error',
                message: getApiErrorMessage(error),
            });
        }
    };

    return (
        <RichTextEditorProvider namespace='package-terms' theme={TermsTheme}>
            <AgreementForm
                initialData={{
                    content: pkg.data?.agreement?.agreement_versions?.[0].content
                }}
                isLoading={pkg.isLoading}
                onSubmit={onSubmit}
            />
        </RichTextEditorProvider>

    );
}

export default PackageTerms;
